import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

interface ClientInfo {
  id: number;
  name: string;
}

interface ClientFile {
  id: number;
  clientId: number;
  originalName: string;
  displayName: string;
  mimeType: string | null;
  size: number;
  createdAt: string;
  updatedAt: string;
}

interface ClientFilesResponse {
  client: ClientInfo;
  files: ClientFile[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const formatDateTime = (value: string) => (
  new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value))
);

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const ClientFileDetails: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { can } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [files, setFiles] = useState<ClientFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchFiles = useCallback(async (page = currentPage) => {
    if (!clientId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<ClientFilesResponse>(`/client-files/clients/${clientId}/files`, {
        params: { page, limit: 50 }
      });
      setClient(response.data.client);
      setFiles(response.data.files);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err: any) {
      console.error('Error fetching files:', err);
      setError(err.response?.data?.message || 'Ошибка при загрузке файлов клиента');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, currentPage]);

  useEffect(() => {
    fetchFiles(currentPage);
  }, [fetchFiles, currentPage]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!clientId || !selectedFiles || selectedFiles.length === 0) return;

    const formData = new FormData();
    Array.from(selectedFiles).forEach((file) => formData.append('files', file));

    setUploading(true);
    setError(null);

    try {
      await axios.post(`/client-files/clients/${clientId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchFiles(1);
      setCurrentPage(1);
    } catch (err: any) {
      console.error('Error uploading files:', err);
      setError(err.response?.data?.message || 'Ошибка при загрузке файлов');
    } finally {
      setUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleDownload = async (file: ClientFile) => {
    try {
      const response = await axios.get(`/client-files/${file.id}/download`, {
        responseType: 'blob'
      });
      downloadBlob(response.data, file.displayName || file.originalName);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Ошибка при скачивании файла');
    }
  };

  const handleRename = async (file: ClientFile) => {
    const nextName = window.prompt('Новое название файла', file.displayName);
    if (!nextName || nextName.trim() === file.displayName) return;

    try {
      await axios.put(`/client-files/${file.id}`, { displayName: nextName.trim() });
      await fetchFiles(currentPage);
    } catch (err: any) {
      console.error('Error renaming file:', err);
      setError(err.response?.data?.message || 'Ошибка при переименовании файла');
    }
  };

  const handleDelete = async (file: ClientFile) => {
    if (!window.confirm(`Удалить файл "${file.displayName}"?`)) return;

    try {
      await axios.delete(`/client-files/${file.id}`);
      await fetchFiles(currentPage);
    } catch (err: any) {
      console.error('Error deleting file:', err);
      setError(err.response?.data?.message || 'Ошибка при удалении файла');
    }
  };

  if (!can('files.view')) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к файлам</p>
      </div>
    );
  }

  if (loading && files.length === 0 && !client) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title={client ? `Файлы: ${client.name}` : 'Файлы клиента'}
        subtitle={`Файлов: ${totalCount}`}
        backLink="/files"
        actions={
          can('files.upload') ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleUpload}
              />
              <button
                type="button"
                className="btn-primary flex items-center"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-1" />
                {uploading ? 'Загрузка...' : 'Загрузить'}
              </button>
            </>
          ) : null
        }
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Файл
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тип
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Размер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Изменен
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && files.length > 0 && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-gray-500">
                    <LoadingSpinner /> Загрузка...
                  </td>
                </tr>
              )}
              {!loading && files.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500">
                    Файлы еще не загружены
                  </td>
                </tr>
              )}
              {!loading && files.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="font-medium break-all">{file.displayName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {file.mimeType || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 text-right">
                    {formatFileSize(file.size)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {formatDateTime(file.updatedAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        className="text-primary hover:text-primary-dark p-1 rounded hover:bg-blue-50"
                        title="Скачать"
                        onClick={() => handleDownload(file)}
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                      {can('files.edit') && (
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                          title="Переименовать"
                          onClick={() => handleRename(file)}
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      )}
                      {can('files.delete') && (
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                          title="Удалить"
                          onClick={() => handleDelete(file)}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default ClientFileDetails;
